import { Icon } from '@iconify/react'
import { useEffect, useState } from 'react'
import type { PendingGiftcardSale } from '../../types/ApiTypes'
import GiftcardDetails from '../components/GiftcardDetails'
import Pagination from '../components/Pagination'
import SkeletonTable from '../components/SkeletonTable'
import { useApi } from '../hooks/useApi'
import Dashboard from '../layout/Dashboard'
import { Content, Heading } from '../styles/OverviewStyle'
import { Badge, MonoSmall, MonoUsername, TableHeadRow, TableRow, TableWrap } from '../styles/TableStyle'
import { formatWithCommas } from '../utils/numberHelpers'

interface GiftcardRow {
  ref: string
  user: string
  card: string
  amount: string
  rate: string
  status: 'Pending'
  date: string
}

export default function PendingGiftcard(): React.JSX.Element {
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState<PendingGiftcardSale | null>(null)
  const { transaction: transactionService } = useApi()
  const [giftcards, setGiftcards] = useState<PendingGiftcardSale[]>([])
  const [loading, setLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(0)
  const pageSize = 20

  const formatDateSimple = (dateStr: string): string => {
    const date = new Date(dateStr)
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`
  }

  const mapGiftcards = (sales: PendingGiftcardSale[]): GiftcardRow[] => {
    return sales.map(s => ({
      ref: s.transactionId,
      user: `@${s.username}`,
      card: s.giftcardType,
      amount: `$${formatWithCommas(s.amountInUsd)}`,
      rate: `₦${formatWithCommas(s.rate)}`,
      status: 'Pending' as const,
      date: formatDateSimple(s.date)
    }))
  }

  const mapped = mapGiftcards(giftcards)
  const filtered = mapped.filter(r =>
    r.ref.toLowerCase().includes(query.toLowerCase()) ||
    r.user.toLowerCase().includes(query.toLowerCase()) ||
    r.card.toLowerCase().includes(query.toLowerCase())
  )

  useEffect(() => {
    const loadGiftcards = async () => {
      setLoading(true)
      const res = await transactionService.getPendingGiftcardSales({ pageNumber: currentPage, pageSize })
      if (res.status === 'success' && res.data) {
        setGiftcards(res.data.giftcardSales)
        setTotalPages(res.data.totalPages)
      }
      setLoading(false)
    }
    loadGiftcards()
  }, [transactionService, currentPage])

  return (
    <Dashboard title="Pending Giftcard / All" icon="solar:card-linear" active="pending_giftcard">
      <Content>
        <Heading>Pending Giftcard Sale</Heading>

        <div className="flex items-center gap-4 mt-6 mb-4">
          <div className="flex items-center gap-2 bg-white border border-[#EDEDEE] rounded-md px-[9px] py-[6px] w-[229px] h-[28px]">
            <Icon icon='solar:magnifer-linear' width={14} className="text-[#B3B3B6] mb-0.5" />
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search"
              className="flex-1 bg-transparent outline-none text-[12px] font-medium tracking-tight placeholder:text-[#B3B3B6]" />
          </div>

          <button
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 h-[28px] text-xs tracking-tight bg-white border border-[#E7E7E9] rounded-lg cursor-pointer hover:bg-[#F9F9FA] transition-colors"
          >
            <span className='text-neutral-900 font-medium'>Export csv</span>
            <Icon icon='solar:upload-linear' width={14} />
          </button>
        </div>

        {loading ? (
          <SkeletonTable />
        ) : (
          <TableWrap>
            <TableHeadRow>
              <div>Trx ID</div>
              <div>Username</div>
              <div>Card Type</div>
              <div>Amount</div>
              <div>Rate</div>
              <div>Status</div>
              <div>Date</div>
              <div></div>
            </TableHeadRow>
            {filtered.map(r => (
              <TableRow key={r.ref} className="hover:bg-[#FAFAFB] transition-colors">
                <MonoSmall>{r.ref}</MonoSmall>
                <MonoUsername>{r.user}</MonoUsername>
                <div className='text-sm text-[#6D6D72]'>{r.card}</div>
                <div className='text-sm text-[#101018]'>{r.amount}</div>
                <div className='text-sm text-[#6D6D72]'>{r.rate}</div>
                <div>
                  <Badge variant='pending'>Pending</Badge>
                </div>
                <div className='text-sm tracking-tight text-[#6D6D72]'>{r.date}</div>
                <div>
                  <button onClick={() => {
                    const sale = giftcards.find(g => g.transactionId === r.ref)
                    if (sale) setSelected(sale)
                  }} className='px-2.5 py-1 h-[26px] text-[12px] rounded-md border border-[#E2E2E4] bg-white hover:bg-[#F9F9FA] font-medium tracking-tight flex items-center gap-1 cursor-pointer'>
                    Review
                    <Icon icon='solar:alt-arrow-right-linear' width={14} />
                  </button>
                </div>
              </TableRow>
            ))}
          </TableWrap>
        )}

        <GiftcardDetails
          open={!!selected}
          onClose={() => setSelected(null)}
          sale={selected || undefined}
          onSuccess={() => {
            setSelected(null)
            const loadGiftcards = async () => {
              setLoading(true)
              const res = await transactionService.getPendingGiftcardSales({ pageNumber: currentPage, pageSize })
              if (res.status === 'success' && res.data) {
                setGiftcards(res.data.giftcardSales)
                setTotalPages(res.data.totalPages)
              }
              setLoading(false)
            }
            loadGiftcards()
          }}
        />

        {!loading && totalPages > 0 && (
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        )}
      </Content>
    </Dashboard>
  )
}
